<template>
  <div class="query-page">
    <section class="filter-panel">
        <div class="filter-title"><strong>筛选条件</strong><span>{{ isSuperAdmin ? '不选设备可查询全部；输入设备号片段并按回车可模糊搜索' : '请选择已绑定设备后查询' }}</span></div>
      <div class="filter-grid water-filter-grid">
        <div class="filter-item">
          <el-select
              v-model="searchParams.deviceId"
              :placeholder="isSuperAdmin ? '全部设备 / 输入设备号片段' : '请选择设备ID'"
              size="large"
              filterable
              :allow-create="isSuperAdmin"
              clearable
              :loading="devicesLoading"
              :no-data-text="deviceLoadFailed ? '设备加载失败，请重试' : '暂无关联设备'"
              @visible-change="onDeviceDropdown"
              style="width: 100%">
            <template #label="{ label }">
              <input class="copyable-device-id" :value="label" readonly aria-label="已选设备ID"
                     @mousedown.stop @click.stop @keydown.stop />
            </template>
            <el-option v-for="deviceId in deviceIds" :key="deviceId" :label="deviceId" :value="deviceId" />
          </el-select>
        </div>
        <label class="filter-item">
          <el-date-picker
              v-model="searchParams.begin"
              type="date"
              placeholder="开始日期"
              size="large"
              :picker-options="pickerOptions"
              value-format="YYYY-MM-DD"
              clearable>
          </el-date-picker>
        </label>
        <label class="filter-item">
          <el-date-picker
              v-model="searchParams.end"
              type="date"
              placeholder="结束日期"
              size="large"
              :picker-options="pickerOptions"
              value-format="YYYY-MM-DD"
              clearable>
          </el-date-picker>
        </label>
      </div>
      <div class="filter-actions">
        <el-button type="primary" size="large" @click="searchWaterData">查询水质数据</el-button>
        <el-button type="success" size="large" plain @click="exportWaterData" :disabled="!waterData || waterData.length === 0">
          导出水质数据
        </el-button>
      </div>
    </section>

    <section class="table-panel">
      <div class="table-heading"><div><h3>水质记录</h3><p>共 {{ pageParams.total }} 条检测数据</p></div></div>
      <el-table
        :data="waterData"
        style="width: 100%; table-layout: auto;"
        :header-cell-style="{textAlign: 'center'}"
        :cell-style="{textAlign: 'center'}" >
      <el-table-column
          prop="id"
          label="id"
          min-width="50"
          max-width="200">
      </el-table-column>
      <el-table-column
          prop="deviceId"
          label="device_id"
          width="150">
      </el-table-column>
      <el-table-column
          prop="customer"
          label="客户名称"
          min-width="120"
          max-width="240">
        <template #default="{ row }">
          {{ row.customer || '' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="fishpondName"
          label="池塘名称"
          min-width="120"
          max-width="240">
        <template #default="{ row }">
          {{ row.fishpondName || '' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="nhNDisplay"
          label="氨氮(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.nhNDisplay || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="nitriteDisplay"
          label="亚硝酸盐(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.nitriteDisplay || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="alkalinityDisplay"
          label="总碱度(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.alkalinityDisplay || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="totalHardnessDisplay"
          label="总硬度(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.totalHardnessDisplay || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="ca2Display"
          label="钙离子(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.ca2Display || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="mg2Display"
          label="镁离子(mg/L)"
          min-width="80"
          max-width="200">
        <template #default="{ row }">
          {{ row.mg2Display || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="phDisplay"
          label="pH"
          min-width="50"
          max-width="120">
        <template #default="{ row }">
          {{ row.phDisplay || '-' }}
        </template>
      </el-table-column>
      <el-table-column
          prop="createTime"
          label="检测时间"
          width="180">
      </el-table-column>
      </el-table>
      <div class="pagination-bar">
      <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pageParams.total"
          :current-page="pageParams.current"
          :page-size="pageParams.pageSize">
      </el-pagination>
      </div>
    </section>
  </div>
</template>

<script>
import {fetchWaterQualityData, exportWaterData} from "@/api/waterQuality";
import api from '@/api';
import {ElMessage} from 'element-plus';
import { API_BASE_URL } from '@/config';
import { readExcelExportResponse } from '@/utils/excelExportResponse';

export default {
  // 数据属性
  data() {
    return {
      deviceIds: [],
      devicesLoading: false,
      deviceLoadFailed: false,
      searchParams: {
        deviceId: "",  // 设备ID
        fishpondId: 0, // 水塘ID
        begin: "",     // 开始时间
        end: "",       // 结束时间
      },
      waterData: [], // 水质数据列表
      pageParams: {
        current: 1,    // 当前页
        pageSize: 10,  // 每页大小
        total: 0,      // 总记录数
      },
      // 定义日期选择器的配置项
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now(); // 禁用未来日期
        },
      },
    };
  },
  computed: {
    isSuperAdmin() {
      return localStorage.getItem('permission') === '0';
    },
    hasMorePages() {
      return this.pageParams.current * this.pageParams.pageSize < this.pageParams.total;
    },
  },
  mounted() {
    this.loadMyDeviceIds();
  },
  methods: {
    async loadMyDeviceIds() {
      if (this.devicesLoading) return;
      this.devicesLoading = true;
      this.deviceLoadFailed = false;
      try {
        const response = await api.get('/web/device/water-data-ids');
        this.deviceIds = response.data || [];
      } catch (error) {
        this.deviceIds = [];
        this.deviceLoadFailed = true;
        ElMessage.error('加载关联设备失败，请重新展开下拉框重试');
      } finally {
        this.devicesLoading = false;
      }
    },
    onDeviceDropdown(visible) {
      if (visible && this.deviceLoadFailed) this.loadMyDeviceIds();
    },
    // 分页控制：每页大小变化时触发
    handleSizeChange(val) {
      this.pageParams.pageSize = val;
      this.searchWaterData(); // 重置为第一页并重新查询
    },

    // 分页控制：当前页变化时触发
    handleCurrentChange(val) {
      this.pageParams.current = val;
      this.searchWaterData(); // 查询当前页的数据
    },

    // 查询水质数据

    // 新增日期格式化方法
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async searchWaterData() {
      // 校验：必须至少传入 deviceId 或 fishpondId
      if (!this.isSuperAdmin && !this.searchParams.deviceId && !this.searchParams.fishpondId) {
        ElMessage.warning("设备ID和水塘ID至少传入一个");
        return;
      }

      // 如果没有传入 begin 或 end，则设置为当天
      if (!this.searchParams.begin) {
        this.searchParams.begin = this.getCurrentDate();
      }
      if (!this.searchParams.end) {
        this.searchParams.end = this.getCurrentDate();
      }

      const requestData = {
        deviceId: this.searchParams.deviceId || undefined,
        fishpondId: this.searchParams.fishpondId || undefined,
        begin: this.searchParams.begin,
        end: this.searchParams.end,
        current: this.pageParams.current,
        pageSize: this.pageParams.pageSize,
      };

      try {
        const response = await fetchWaterQualityData(requestData);

        // 处理返回的数据
        if (response.data && response.data.records) {
          this.waterData = response.data.records;
          this.pageParams.total = response.data.total;
          ElMessage.success("查询成功，共" + response.data.total + "条记录");
        } else {
          ElMessage.info("未查询到水质数据");
          this.waterData = [];
        }
      } catch (error) {
        console.error("查询水质数据失败:", error);
        ElMessage.error('查询数据失败，请稍后再试');

        // 如果有 token 相关的错误，可以在此处做额外处理
        if (error.message === '用户未登录，token 不存在') {
          ElMessage.warning('请登录后再查询数据');
          this.$router.push({name: 'Login'});
        }
      }
    },

    // 导出水质数据
    async exportWaterData() {
      // 校验：必须有查询结果才能导出
      if (!this.waterData || this.waterData.length === 0) {
        ElMessage.warning("没有数据可导出，请先查询数据");
        return;
      }

      // 准备导出参数（使用当前查询条件）
      const exportParams = {
        deviceId: this.searchParams.deviceId,
        fishpondId: this.searchParams.fishpondId,
        begin: this.searchParams.begin,
        end: this.searchParams.end,
        // 导出全部数据，不使用分页（根据后端接口调整）
        // 注意：如果后端接口不支持不分页，可能需要调整为获取所有页数据
        pageSize: this.pageParams.total > 10000 ? 10000 : this.pageParams.total // 限制最大导出数量
      };

      try {
        // 显示加载状态
        ElMessage.info("正在准备数据，请稍候...");

        // 调用导出API（使用已有的exportWaterData函数）
        // const response = await exportWaterData(exportParams);

        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/web/waterdata/export`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'token': token, // 使用后端期望的 token 头
          },
          body: JSON.stringify(exportParams),
          responseType: 'blob', // 关键配置：告知浏览器接收二进制流
        });

        // 创建Blob对象
        // const blob = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        // 关键步骤：通过 response.blob() 获取 Blob 对象
        //处理Blob响应
        const blob = await readExcelExportResponse(response);
        // console.log('@@@@@@@@@blob类型:', blob instanceof Blob); // 应输出 true
        // console.log('@@@@@@@@@blob.size:', blob.size); // 应显示正确大小
        // 创建下载链接
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);

        // 设置文件名（包含时间戳，避免重复）
        const date = new Date();
        const formattedDate = date.getFullYear() +
            String(date.getMonth() + 1).padStart(2, '0') +
            String(date.getDate()).padStart(2, '0') + '_' +
            String(date.getHours()).padStart(2, '0') +
            String(date.getMinutes()).padStart(2, '0') +
            String(date.getSeconds()).padStart(2, '0');
        link.download = `水质数据_${formattedDate}.xlsx`;

        // 触发下载
        document.body.appendChild(link);
        link.click();

        // 清理资源
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        ElMessage.success("导出成功");
      } catch (error) {
        //错误处理
        console.error("导出失败:", error);

        // 处理可能的HTTP错误
        if (error.status || error.code || error.response) {
          if (error.status === 401 || error.response?.status === 401 || Number(error.code) === 1003) {
            ElMessage.error("登录状态已过期，请重新登录");
            this.$router.push({name: 'Login'});
            return;
          } else if (error.status === 403 || error.response?.status === 403 || Number(error.code) === 3001) {
            ElMessage.error("没有导出权限");
            return;
          }
        }

        ElMessage.error(error.message || "导出失败，请稍后再试");
      }
    },

    // 获取当前日期，格式为 YYYY-MM-DD
    getCurrentDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.copyable-device-id {
  width: 100%;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: text;
  pointer-events: auto;
  user-select: text;
  -webkit-user-select: text;
}
.query-page { color: #263b4b; }
.table-heading p { margin: 6px 0 0; color: #7a8b98; font-size: 13px; }
.filter-panel, .table-panel { padding: 22px 24px; border: 1px solid #e4eaed; border-radius: 14px; background: #fff; box-shadow: 0 7px 24px rgba(31, 61, 78, .045); }
.filter-panel { margin-bottom: 18px; }
.filter-title, .table-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.filter-title { margin-bottom: 18px; }
.filter-title strong, .table-heading h3 { margin: 0; font-size: 16px; }
.filter-title span { color: #8a99a4; font-size: 12px; }
.filter-grid { display: grid; grid-template-columns: repeat(3, minmax(180px, 1fr)); gap: 18px; }
.filter-item { display: flex; min-width: 0; flex-direction: column; color: #566b79; font-size: 13px; }
.filter-item :deep(.el-select), .filter-item :deep(.el-date-editor) { width: 100%; }
.filter-item :deep(.el-input__wrapper) { border-radius: 9px; box-shadow: 0 0 0 1px #d8e1e5 inset; }
.filter-item :deep(.el-input__wrapper:hover) { box-shadow: 0 0 0 1px #91bbb7 inset; }
.filter-item :deep(.el-input__wrapper.is-focus) { box-shadow: 0 0 0 1px #0a7b73 inset; }
.filter-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.filter-actions :deep(.el-button + .el-button) { margin-left: 0; }
.table-heading { margin-bottom: 16px; }
.pagination-bar { display: flex; justify-content: flex-end; padding-top: 20px; overflow-x: auto; }
:deep(.el-table td), :deep(.el-table th) {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
@media (max-width: 960px) { .filter-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 620px) {
  .filter-panel, .table-panel { padding: 18px 16px; }
  .filter-grid { grid-template-columns: 1fr; gap: 14px; }
  .filter-title { align-items: flex-start; flex-direction: column; }
  .filter-actions :deep(.el-button) { flex: 1; }
}
</style>
